"use client";

import React, { useEffect, useState, useRef, useCallback } from "react";
import Logo from "@/app/components/Logo";
import PageContainer from "@/app/components/ui-components/PageContainer";
import AppVersionFooter from "@/app/components/AppVersionFooter";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import { FaLinkedinIn } from "react-icons/fa";
import { FcGoogle } from "react-icons/fc";
import { useRouter, useSearchParams } from "next/navigation";
import { signIn, useSession } from "next-auth/react";
import { track } from "@/lib/analytics/track";
import { EVENTS } from "@/lib/analytics/events";
import {
  Typography,
  Box,
  CircularProgress,
  Chip,
  Button,
  useMediaQuery,
  Fade,
  useTheme,
} from "@mui/material";
import EmailAuthSection from "@/app/components/auth/EmailAuthSection";
import linkedInButtonStyles from "@/app/components/auth/linkedInButtonStyles";
import useLastLoginProvider from "@/lib/hooks/useLastLoginProvider";

// Constants
const ERROR_MESSAGES = {
  AccountBlocked:
    "Your account has been suspended. If you think this was done in error, please reach out to us at support@meex.co and we'll look into it.",
  AccessDenied:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  OAuthCallback:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  OAuthSignin:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  Callback:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  SignInError:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  access_denied:
    "No problem, LinkedIn sign-in was cancelled. You can try again whenever you're ready.",
  TooManyRequests:
    "Too many sign-in attempts. Please wait a few minutes and try again.",
  CredentialsSignin:
    "This sign-in link is invalid or has already been used. Please request a new one.",
  AccountLinkRequired:
    "We found a possible existing account. Sign in using your original method, then verify and connect your LinkedIn account in settings. If you cannot access it, contact support@meex.co.",
  AccountIdentityConflict:
    "We found conflicting account records and have stopped this sign-in to protect your meetings. Sign in using your original method or contact support@meex.co to resolve the accounts.",
  FirestoreError:
    "We could not check your account right now. Please try again. No new account was created.",
  default: "Sign-in wasn't completed. You can try again anytime.",
};

const ERROR_TITLES = {
  AccountBlocked: "Account suspended",
  AccountLinkRequired: "Verify your existing account",
  AccountIdentityConflict: "Account needs attention",
  FirestoreError: "Account check unavailable",
  AccessDenied: "Sign-in cancelled",
  OAuthCallback: "Sign-in cancelled",
  OAuthSignin: "Sign-in cancelled",
  Callback: "Sign-in cancelled",
  SignInError: "Sign-in cancelled",
  access_denied: "Sign-in cancelled",
  default: "Sign-in not completed",
};

// Components
const AuthErrorMessage = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const error = searchParams?.get("error");
  const emailConfirmed = searchParams?.get("emailConfirmed");
  const emailLinkError = searchParams?.get("emailLinkError");

  // email link confirmation / error (from confirm-signin-email route)
  const [emailDialogOpen, setEmailDialogOpen] = useState(
    emailConfirmed === "1" || !!emailLinkError,
  );
  const [authErrorOpen, setAuthErrorOpen] = useState(Boolean(error));

  useEffect(() => {
    setAuthErrorOpen(Boolean(error));
  }, [error]);

  if (emailDialogOpen) {
    const isSuccess = emailConfirmed === "1";
    return (
      <ConfirmDialog
        open={emailDialogOpen}
        onClose={() => setEmailDialogOpen(false)}
        onConfirm={() => setEmailDialogOpen(false)}
        title={isSuccess ? "Email confirmed" : "Email link error"}
        body={
          isSuccess
            ? "Your sign-in email has been confirmed. Sign in to continue to your settings."
            : decodeURIComponent(emailLinkError || "Something went wrong.")
        }
        confirmButtonText="Okay"
        showIgnoreButton={false}
        showCloseIcon={false}
        modalStyle="toast"
        confirmButtonVariant="text"
      />
    );
  }

  // standard NextAuth sign-in errors (AccountBlocked, TooManyRequests, etc.)
  if (!error) return null;
  const errorMessage = ERROR_MESSAGES[error] || ERROR_MESSAGES.default;
  const errorTitle = ERROR_TITLES[error] || ERROR_TITLES.default;
  const closeErrorDialog = () => {
    setAuthErrorOpen(false);
    const params = new URLSearchParams(searchParams?.toString());
    params.delete("error");
    const query = params.toString();
    router.replace(query ? `/login?${query}` : "/login", { scroll: false });
  };
  return (
    <ConfirmDialog
      open={authErrorOpen}
      onClose={closeErrorDialog}
      onConfirm={closeErrorDialog}
      title={errorTitle}
      body={errorMessage}
      confirmButtonText="Okay"
      showIgnoreButton={false}
      showCloseIcon={false}
      modalStyle="toast"
      confirmButtonVariant="text"
    />
  );
};

const LinkedInAuthButton = ({
  isLoading,
  onLogin,
  isMobile,
  label = "Sign in with LinkedIn",
  disabled = false,
  lastUsed = false,
}) => {
  const buttonStyles = {
    px: isMobile ? 2 : 2.5,
    pl: isMobile ? 2 : 2.5,
    typography: "button",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    borderRadius: "12px",
    width: "100%",
    height: isMobile ? 56 : 52,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",
    transition: isMobile
      ? "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
      : "background-color 0.3s ease",
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "text.primary",
      outlineOffset: 3,
    },
    ...linkedInButtonStyles(),
  };

  return (
    <Button
      variant="contained"
      size="medium"
      sx={{
        ...buttonStyles,
        position: "relative",
        overflow: "visible",
        gap: 1.5,
      }}
      onClick={onLogin}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
    >
      {lastUsed && (
        <Chip
          label="Last used"
          size="small"
          sx={{
            position: isMobile ? "static" : "absolute",
            order: 2,
            flexShrink: 0,
            top: isMobile ? undefined : "50%",
            transform: isMobile ? undefined : "translateY(-50%)",
            right: isMobile ? 8 : 16,
            height: 20,
            fontSize: "0.68rem",
            zIndex: 2,
            backgroundColor: "transparent",
            color: "inherit",
            border: "1px solid",
            borderColor: "currentColor",
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: isMobile ? 1.5 : 2,
          width: "100%",
          minWidth: 0,
          color: "inherit",
          "& > svg": { flexShrink: 0 },
        }}
      >
        {isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <FaLinkedinIn size={20} />
        )}
        <Box
          component="span"
          sx={{
            color: "inherit",
            whiteSpace: isMobile ? "normal" : "nowrap",
            textAlign: "left",
          }}
        >
          {label}
        </Box>
      </Box>
    </Button>
  );
};

const GoogleAuthButton = ({
  isLoading,
  onLogin,
  isMobile,
  label = "Sign in with Google",
  disabled = false,
  lastUsed = false,
}) => {
  const theme = useTheme();

  const buttonStyles = {
    px: isMobile ? 2 : 2.5,
    pl: isMobile ? 2 : 2.5,
    typography: "button",
    fontSize: "0.875rem",
    lineHeight: 1.5,
    backgroundColor: "action.hover",
    color: "text.primary",
    border: "1.5px solid",
    borderColor: "transparent",
    borderRadius: "12px",
    width: "100%",
    height: isMobile ? 56 : 52,
    fontWeight: 600,
    textTransform: "none",
    boxShadow: "none",
    transition: "background-color 0.3s ease, border-color 0.3s ease",
    "&:hover": {
      backgroundColor: "action.selected",
      borderColor: "transparent",
      color: "text.primary",
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: "text.primary",
      outlineOffset: 3,
    },
    "&:disabled": { opacity: 0.5 },
  };

  return (
    <Button
      variant="outlined"
      size="medium"
      sx={{
        ...buttonStyles,
        position: "relative",
        overflow: "visible",
        gap: 1.5,
      }}
      onClick={onLogin}
      disabled={disabled || isLoading}
      aria-busy={isLoading}
    >
      {lastUsed && (
        <Chip
          label="Last used"
          size="small"
          sx={{
            position: isMobile ? "static" : "absolute",
            order: 2,
            flexShrink: 0,
            top: isMobile ? undefined : "50%",
            transform: isMobile ? undefined : "translateY(-50%)",
            right: isMobile ? 8 : 16,
            height: 20,
            fontSize: "0.68rem",
            zIndex: 2,
            backgroundColor: "transparent",
            color: "inherit",
            border: "1px solid",
            borderColor: "currentColor",
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          gap: isMobile ? 1.5 : 2,
          width: "100%",
          minWidth: 0,
          "& > svg": { flexShrink: 0 },
        }}
      >
        {isLoading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <FcGoogle size={20} />
        )}
        <Box
          component="span"
          sx={{ whiteSpace: isMobile ? "normal" : "nowrap", textAlign: "left" }}
        >
          {label}
        </Box>
      </Box>
    </Button>
  );
};

// Full-page spinner shown while a magic-link sign-in is being processed.
const MagicLinkLoadingScreen = () => (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2.5,
      backgroundColor: "background.default",
      zIndex: 9999,
    }}
  >
    <Logo width={60} height={60} />
    <CircularProgress size={22} thickness={3} />
    <Typography variant="body2" color="text.secondary" sx={{ mt: -0.5 }}>
      Signing you in&hellip;
    </Typography>
  </Box>
);

const PermissionChecklist = ({ isMobile, isLinkingMode, prefixText }) => {
  const theme = useTheme();
  const greyColor = theme.palette.grey?.[350] || theme.palette.grey[500];

  if (isMobile) {
    return (
      <Fade in timeout={1000}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 0.8,
            mt: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: greyColor, lineHeight: 1.4, fontSize: "0.82rem" }}
          >
            <a
              href="https://meex.co/terms-of-service/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              Terms of Service
            </a>
            {" · "}
            <a
              href="https://meex.co/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "inherit", textDecoration: "underline" }}
            >
              Privacy Policy
            </a>
          </Typography>
        </Box>
      </Fade>
    );
  }

  return (
    <Typography
      variant="body2"
      sx={{
        color: "text.secondary",
        mt: 0,
        lineHeight: 1.6,
        fontSize: "0.75rem",
      }}
    >
      {prefixText && (
        <>
          {prefixText}
          {" · "}
        </>
      )}
      <a
        href="https://meex.co/terms-of-service/"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", textDecoration: "underline" }}
      >
        Terms of Service
      </a>
      {" · "}
      <a
        href="https://meex.co/privacy-policy/"
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "inherit", textDecoration: "underline" }}
      >
        Privacy Policy
      </a>
    </Typography>
  );
};

const MobileLayout = ({
  isLoading,
  onLogin,
  isGoogleLoading,
  onGoogleLogin,
  isLinkingMode,
  lastProvider,
  onEmailLogin,
}) => (
  <Box
    sx={{
      minHeight: "100dvh",
      width: "100%",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      backgroundColor: "background.paper",
      position: "relative",
      zIndex: (theme) => theme.zIndex.drawer + 2,
      pt: "calc(env(safe-area-inset-top, 0px) + clamp(64px, 10svh, 96px))",
    }}
  >
    <Box component="main" sx={{ width: "100%", maxWidth: 468, px: 3 }}>
      <Box sx={{ mb: 2.5 }}>
        <Logo width={44} height={44} />
      </Box>
      <Typography
        component="h1"
        sx={{
          fontSize: "28px",
          fontWeight: 600,
          lineHeight: 1.25,
          letterSpacing: "-0.5px",
        }}
      >
        {isLinkingMode ? "Verify your LinkedIn profile" : "Welcome Meex"}
      </Typography>
      <Typography
        sx={{
          color: "text.secondary",
          mt: 1.5,
          mb: 4,
          fontSize: "15px",
          lineHeight: 1.6,
        }}
      >
        {isLinkingMode
          ? "Connect LinkedIn to finish setting up your professional profile."
          : "Find people beyond your network and book time to connect."}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <LinkedInAuthButton
          isLoading={isLoading}
          disabled={isLoading || isGoogleLoading}
          onLogin={onLogin}
          isMobile={true}
          label="Continue with LinkedIn"
          lastUsed={lastProvider === "linkedin"}
        />
        {!isLinkingMode && (
          <>
            <GoogleAuthButton
              isLoading={isGoogleLoading}
              disabled={isLoading || isGoogleLoading}
              onLogin={onGoogleLogin}
              isMobile={true}
              label="Continue with Google"
              lastUsed={lastProvider === "google"}
            />
            <EmailAuthSection
              disabled={isLoading || isGoogleLoading}
              isMobile={true}
              presentation="button"
              onSignInStart={onEmailLogin}
              lastUsed={lastProvider === "email"}
            />
          </>
        )}
      </Box>
      {!isLinkingMode && (
        <Typography
          sx={{
            color: "text.secondary",
            mt: 2.5,
            fontSize: "13px",
            lineHeight: 1.6,
          }}
        >
          New to Meex? Google and email sign-up include LinkedIn verification.
        </Typography>
      )}
      <Box sx={{ mt: 2 }}>
        <PermissionChecklist isMobile={false} isLinkingMode={isLinkingMode} />
      </Box>
      <AuthErrorMessage />
    </Box>
    <AppVersionFooter
      sx={{
        mt: "auto",
        pt: 4,
        pb: "max(16px, env(safe-area-inset-bottom))",
        px: 3,
      }}
    />
  </Box>
);

const DesktopLayout = ({
  isLoading,
  onLogin,
  isGoogleLoading,
  onGoogleLogin,
  isLinkingMode,
  lastProvider,
  onEmailLogin,
}) => (
  <Box
    sx={{
      minHeight: "100dvh",
      display: "flex",
      flexDirection: "column",
      backgroundColor: (theme) =>
        theme.palette.mode === "dark"
          ? theme.palette.background.default
          : "#F7F7F5",
    }}
  >
    <Box
      component="main"
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 3,
        py: 3,
      }}
    >
      <Box
        component="section"
        aria-labelledby="sign-in-title"
        sx={{
          width: "100%",
          maxWidth: 620,
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.04)",
        }}
      >
        <Box
          sx={{
            px: 4,
            pt: 4,
            pb: 0,
          }}
        >
          <Box sx={{ mb: 2.5 }}>
            <Logo width={44} height={44} />
          </Box>
          <Typography
            id="sign-in-title"
            component="h1"
            sx={{
              maxWidth: 360,
              fontSize: "30px",
              fontWeight: 600,
              lineHeight: 1.2,
              letterSpacing: "-0.75px",
            }}
          >
            {isLinkingMode ? "Verify your LinkedIn profile" : "Welcome Meex"}
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              color: "text.secondary",
              fontSize: "15px",
              lineHeight: 1.6,
            }}
          >
            {isLinkingMode
              ? "Connect LinkedIn to finish setting up your professional profile."
              : "Find people beyond your network and book time to connect."}
          </Typography>
        </Box>
        <Box sx={{ p: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <LinkedInAuthButton
              isLoading={isLoading}
              disabled={isLoading || isGoogleLoading}
              onLogin={onLogin}
              isMobile={false}
              lastUsed={lastProvider === "linkedin"}
              label="Continue with LinkedIn"
            />
            {!isLinkingMode && (
              <>
                <GoogleAuthButton
                  isLoading={isGoogleLoading}
                  disabled={isLoading || isGoogleLoading}
                  onLogin={onGoogleLogin}
                  isMobile={false}
                  lastUsed={lastProvider === "google"}
                  label="Continue with Google"
                />
                <EmailAuthSection
                  disabled={isLoading || isGoogleLoading}
                  onSignInStart={onEmailLogin}
                  lastUsed={lastProvider === "email"}
                  presentation="button"
                />
              </>
            )}
          </Box>
          {!isLinkingMode && (
            <Typography
              sx={{
                mt: 2.5,
                color: "text.secondary",
                fontSize: "13px",
                lineHeight: 1.6,
              }}
            >
              New to Meex? Google and email sign-up include LinkedIn
              verification.
            </Typography>
          )}
          <Box sx={{ mt: 2 }}>
            <PermissionChecklist
              isMobile={false}
              isLinkingMode={isLinkingMode}
            />
          </Box>
        </Box>
        <AuthErrorMessage />
      </Box>
    </Box>
    <AppVersionFooter />
  </Box>
);

export default function Login() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const step = searchParams?.get("step");
  const isLinkingMode = step === "link-linkedin";

  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isMagicSignInLoading, setIsMagicSignInLoading] = useState(false);
  const {
    lastProvider: displayedLastProvider,
    rememberProvider,
  } = useLastLoginProvider();
  const hasRequested = useRef(false);
  const hasGoogleRequested = useRef(false);
  const hasMagicSignInRef = useRef(false);

  useEffect(() => {
    // Redirect once the user has a real LinkedIn sub (fully authenticated).
    // Using session.user.sub instead of !isLinkingMode so we also redirect
    // after the Google→LinkedIn link flow completes (sub was empty during
    // linking, becomes the LinkedIn sub once the link is done).
    if (status === "authenticated" && session?.user?.sub) {
      const paramCb = searchParams?.get("callbackUrl");
      const pendingParam = searchParams?.get("pending");
      const dest = paramCb && paramCb.startsWith("/") ? paramCb : "/";

      if (pendingParam) {
        // Safety-net: if the NextAuth cookie was lost during the LinkedIn
        // OAuth redirect, wasLinkingGoogle won't have run. Call the
        // complete-google-link endpoint to finish the link now.
        // It's idempotent — safe to call even if wasLinkingGoogle already ran.
        fetch("/api/user/complete-google-link", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pending: pendingParam }),
          credentials: "include",
        }).finally(() => router.push(dest));
      } else {
        router.push(dest);
      }
    }
  }, [status, session?.user?.sub, router, searchParams]);

  // Magic-link auto-sign-in: when the user clicks the emailed link they land on
  // /login?magic_token=UUID. Detect it and call signIn immediately.
  useEffect(() => {
    const magicToken = searchParams?.get("magic_token");
    if (
      !magicToken ||
      status !== "unauthenticated" ||
      hasMagicSignInRef.current
    )
      return;
    hasMagicSignInRef.current = true;
    setIsMagicSignInLoading(true);
    const paramCb = searchParams?.get("callbackUrl");
    const callbackUrl = paramCb && paramCb.startsWith("/") ? paramCb : "/";
    track(EVENTS.SIGNUP_STARTED, {
      provider: "magic-email",
      trigger: "magic_link_clicked",
      callback_url: callbackUrl,
      entry_url:
        typeof window !== "undefined" ? window.location.href : undefined,
      entry_pathname:
        typeof window !== "undefined" ? window.location.pathname : undefined,
      entry_referrer:
        typeof document !== "undefined" ? document.referrer : undefined,
    });
    signIn("magic-email", { token: magicToken, callbackUrl }).catch(() => {
      // signIn() rejects on network error; NextAuth errors redirect back here
      // with ?error=CredentialsSignin so AuthErrorMessage handles them.
      track(EVENTS.LOGIN_FAILED, {
        provider: "magic-email",
        reason: "signIn_rejected",
      });
      setIsMagicSignInLoading(false);
      hasMagicSignInRef.current = false;
    });
  }, [status, searchParams]);

  const handleLogin = useCallback(async () => {
    if (hasRequested.current || isButtonLoading) return;

    hasRequested.current = true;
    setIsButtonLoading(true);
    rememberProvider("linkedin");

    try {
      // If NextAuth redirected here with a callbackUrl query param (e.g. from
      // /s/:requestId), honour that URL so the user lands on the right page
      // after OAuth. Validate it starts with "/" to prevent open-redirect.
      const paramCb = searchParams?.get("callbackUrl");
      const pendingParam = searchParams?.get("pending");
      // When a pending Google link is in progress, redirect to /link-complete
      // (not /login) after LinkedIn OAuth. NextAuth refuses to redirect back
      // to the sign-in page (/login) to prevent loops — /link-complete is a
      // neutral page that calls complete-google-link then forwards to dest.
      const callbackUrl =
        isLinkingMode && pendingParam
          ? `/link-complete?pending=${encodeURIComponent(pendingParam)}${
              paramCb && paramCb.startsWith("/")
                ? `&callbackUrl=${encodeURIComponent(paramCb)}`
                : ""
            }`
          : paramCb && paramCb.startsWith("/")
          ? paramCb
          : window.location.href;
      track(EVENTS.SIGNUP_STARTED, {
        provider: "linkedin",
        is_linking_mode: Boolean(isLinkingMode),
        callback_url: callbackUrl,
        entry_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        entry_pathname:
          typeof window !== "undefined" ? window.location.pathname : undefined,
        entry_referrer:
          typeof document !== "undefined" ? document.referrer : undefined,
      });
      await signIn("linkedin", {
        callbackUrl,
      });
    } catch (error) {
      console.error("Login failed:", error);
      track(EVENTS.LOGIN_FAILED, {
        provider: "linkedin",
        reason: error?.message?.slice(0, 200) || "unknown",
      });
      hasRequested.current = false;
      setIsButtonLoading(false);
    }
  }, [isButtonLoading, rememberProvider]);

  const handleGoogleLogin = useCallback(async () => {
    if (hasGoogleRequested.current || isGoogleLoading) return;

    hasGoogleRequested.current = true;
    setIsGoogleLoading(true);
    rememberProvider("google");

    try {
      const paramCb = searchParams?.get("callbackUrl");
      const callbackUrl =
        paramCb && paramCb.startsWith("/") ? paramCb : window.location.href;
      track(EVENTS.SIGNUP_STARTED, {
        provider: "google",
        callback_url: callbackUrl,
        entry_url:
          typeof window !== "undefined" ? window.location.href : undefined,
        entry_pathname:
          typeof window !== "undefined" ? window.location.pathname : undefined,
        entry_referrer:
          typeof document !== "undefined" ? document.referrer : undefined,
      });
      await signIn("google", { callbackUrl });
    } catch (error) {
      console.error("Google login failed:", error);
      track(EVENTS.LOGIN_FAILED, {
        provider: "google",
        reason: error?.message?.slice(0, 200) || "unknown",
      });
      hasGoogleRequested.current = false;
      setIsGoogleLoading(false);
    }
  }, [isGoogleLoading, rememberProvider]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      const target = e.target;
      const isInteractive =
        target?.isContentEditable ||
        target?.closest?.("button, a, input, textarea, select");
      if (
        e.key === "Enter" &&
        !e.defaultPrevented &&
        !isInteractive &&
        !isButtonLoading &&
        !isGoogleLoading
      ) {
        e.preventDefault();
        handleLogin();
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [handleLogin, isButtonLoading, isGoogleLoading]);

  const layoutProps = {
    isLoading: isButtonLoading,
    onLogin: handleLogin,
    isGoogleLoading,
    onGoogleLogin: handleGoogleLogin,
    isLinkingMode,
    lastProvider: displayedLastProvider,
    onEmailLogin: () => rememberProvider("email"),
  };

  // Don't render login UI while session is resolving or an authenticated
  // user is being redirected — prevents the brief login-page flash.
  // Exception: linking mode must always render so the user can complete
  // the LinkedIn verification step.
  if (status === "loading") return null;
  // Magic-link sign-in in progress — show spinner instead of blank page.
  if (isMagicSignInLoading) return <MagicLinkLoadingScreen />;
  if (status === "authenticated" && session?.user?.sub) return null;

  return isMobile ? (
    <MobileLayout {...layoutProps} />
  ) : (
    <PageContainer title="Meex" description="Sign in to Meex">
      <DesktopLayout {...layoutProps} />
    </PageContainer>
  );
}
